import { ActionIcon, Badge, Group, Paper, ScrollArea, Stack, Table, Text, Title } from '@mantine/core'
import { TbX } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import type { ComparedDetectionModel, DetectionComparisonEntity } from '@/api/main/types.gen'
import { formatDateTime } from '@/utils/datetime'
import { categoryKey, entityColor, entityLabel, formatMetres } from './comparison'
import { useCategoryLabel } from './MatchingControls'

export const DETAIL_PANEL_WIDTH = 360

type Props = {
  entity: DetectionComparisonEntity
  firstDriveId: number
  secondDriveId: number
  onClose: () => void
}

type Row = [string, string | number | null | undefined]

const KeyValueTable = ({ rows }: { rows: Row[] }) => (
  <Table withRowBorders={false} verticalSpacing={2} fz="sm">
    <Table.Tbody>
      {rows.map(([key, value]) => (
        <Table.Tr key={key}>
          <Table.Td c="dimmed" w="45%">
            {key}
          </Table.Td>
          <Table.Td>{value === null || value === undefined || value === '' ? '-' : String(value)}</Table.Td>
        </Table.Tr>
      ))}
    </Table.Tbody>
  </Table>
)

const formatCoordinates = ({ latitude, longitude }: { latitude: number; longitude: number }) =>
  `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`

export function EntityDetail({ entity, firstDriveId, secondDriveId, onClose }: Props) {
  const { t } = useTranslation('detectionComparison')
  const categoryLabel = useCategoryLabel(firstDriveId, secondDriveId)

  const matchRows: Row[] = [[t('detail.drives'), entity.drive_ids.join(', ')]]
  if (entity.members.length === 2) {
    matchRows.push(
      [
        t('detail.matched'),
        entity.is_same_drive ? t('detail.withinDrive', { id: entity.drive_ids[0] }) : t('detail.acrossDrives'),
      ],
      [t('detail.separation'), formatMetres(entity.separation_m, 2)],
      [t('detail.editDistance'), entity.edit_distance]
    )
  }

  const memberRows = (member: ComparedDetectionModel): Row[] => [
    [t('detail.detectionId'), member.detection_id],
    [t('detail.plate'), member.plate],
    [t('detail.observedAt'), formatDateTime(member.observed_at)],
    [t('detail.center'), formatCoordinates(member.center)],
    [t('detail.length'), member.length_m.toFixed(2)],
  ]

  return (
    <Paper
      shadow="md"
      radius="md"
      p="md"
      withBorder
      style={{ width: DETAIL_PANEL_WIDTH, maxHeight: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div>
              <Title order={4}>{entityLabel(entity) || t('list.noPlate')}</Title>
              <Badge mt={4} color={entityColor(entity, firstDriveId)} variant="filled">
                {categoryLabel(categoryKey(entity, firstDriveId))}
              </Badge>
            </div>
            <ActionIcon variant="subtle" color="gray" onClick={onClose} aria-label={t('detail.close')}>
              <TbX size={18} />
            </ActionIcon>
          </Group>

          <Text fw={600} size="sm">
            {t('detail.match')}
          </Text>
          <KeyValueTable rows={matchRows} />

          {entity.members.map(member => (
            <div key={member.detection_id}>
              <Text fw={600} size="sm">
                {t('display.drive', { id: member.drive_id })}
              </Text>
              <KeyValueTable rows={memberRows(member)} />
            </div>
          ))}

          {entity.rejected.length > 0 && (
            <div>
              <Text fw={600} size="sm">
                {t('detail.notMatchedWith')}
              </Text>
              <Table withRowBorders={false} verticalSpacing={2} fz="sm">
                <Table.Tbody>
                  {entity.rejected.map(candidate => (
                    <Table.Tr key={candidate.detection_id}>
                      <Table.Td>
                        {candidate.plate || t('list.noPlate')} ({t('display.drive', { id: candidate.drive_id })})
                      </Table.Td>
                      <Table.Td c="dimmed">
                        {t('detail.candidate', {
                          edit: candidate.edit_distance,
                          distance: candidate.distance_m.toFixed(2),
                          reason: candidate.is_counterpart_matched
                            ? t('detail.counterpartMatched')
                            : t('detail.overDistance'),
                        })}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Stack>
      </ScrollArea>
    </Paper>
  )
}
